import { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import TotalEnergiesContext from "../../../core/store/context/TotalEnergiesContext";
import {
  getCallCenterDocuments,
  getManagementDetail as getManagementDetailFetch,
} from "../../../core/services/managements/managements.service";
import {
  ManagementCupsInfo,
  ManagementDetail,
  NewDocumentData,
  RequestStateDescriptionData,
} from "../../../core/models/managements/managements.model";
import {
  formatManagementDetail,
  getIncidenceIconTitle,
  getManagementTitleText,
  getManagementsRequestTypeData,
  incedenceTexts,
  initialManagementDetail,
} from "../../../utils/components/managements/managements.util";
import { Box, Stack } from "@mui/material";
import theme from "../../../muiTheme";
import "./ManagementsDetail.scss";
import ManagementUpdateHeader from "../../ManagementsUpdate/ManagementsUpdateHeader/ManagementsUpdateHeader";
import ManagementsRequestState from "../ManagementsRequestState/ManagementsRequestState";
import {
  MANAGEMENTS_REGISTER_TYPE_CLAIMS,
  MANAGEMENTS_STATE_ERROR,
  MANAGEMENTS_STATE_OPEN,
  MANAGEMENTS_STATE_PENDING,
  MANAGEMENTS_STATE_PENDING_CLIENT,
  MANAGEMENTS_STATE_PENDING_COMMERCIAL,
  MANAGEMENTS_STATE_PENDING_DISTRIBUTION,
  MANAGEMENTS_SUBTYPE_UPDATE_CONTACT_TEXT,
  MANAGEMENTS_SUBTYPE_UPDATE_POWERS_TEXT,
} from "../../../core/constants/constants";
import { OwnerDataCups } from "../../../core/models/ownerData/ownerData.model";
import { initialOwnerDataCup } from "../../../utils/ownerData/ownerData.util";

const ManagementsDetail = () => {
  const { idIssue } = useParams();
  const {
    userData,
    ownerData,
    isAgent,
    managementsDetailHeader: {
      recordTextType,
      claimTextSubType,
      incidenceText,
      idSubType,
    },
  } = useContext(TotalEnergiesContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);
  const [titleHeader, setTitleHeader] = useState<string>("");
  const [managementDetail, setManagementDetail] = useState<ManagementDetail>(
    initialManagementDetail,
  );
  const [requestStateData, setRequestStateData] =
    useState<RequestStateDescriptionData>({
      title: "",
      description: "",
    });
  const [ownerDataCup, setOwnerDataCup] =
    useState<OwnerDataCups>(initialOwnerDataCup);
  //ADD DIRECCION DE CORRESPONDENCIA
  const showManagementRequestInfo = ![
    MANAGEMENTS_SUBTYPE_UPDATE_CONTACT_TEXT,
  ].includes(claimTextSubType);
  const isUpdatePowersManagement =
    MANAGEMENTS_SUBTYPE_UPDATE_POWERS_TEXT === claimTextSubType;

  const [hasDocuments, setHasDocuments] = useState<boolean>(false);
  const [refferenceNumber, setRefferenceNumber] = useState<number>(0);
  const initialDocumentData: NewDocumentData[] = [];
  const [filesAttached, setFilesAttached] =
    useState<NewDocumentData[]>(initialDocumentData);

  const getManagementDetail = useCallback(
    async (
      idIssue: number,
      token: string,
      claimTextSubType: string,
      isUpdatePowersManagement: boolean,
      abortSignal: AbortSignal,
    ) => {
      await getManagementDetailFetch(idIssue, token, abortSignal)
        .then(async (managementDetail) => {
          let formattedManagementDetail = initialManagementDetail;
          if (Array.isArray(managementDetail) && managementDetail.length > 0) {
            formattedManagementDetail = formatManagementDetail(
              managementDetail[0],
            );
          } else {
            formattedManagementDetail =
              formatManagementDetail(managementDetail);
          }

          setManagementDetail(formattedManagementDetail);

          //Set has documents to download
          if (
            formattedManagementDetail.documents.some(
              (document) => document.FileName != null,
            )
          ) {
            setHasDocuments(true);
            setRefferenceNumber(
              formattedManagementDetail.refferenceNumber
                ? formattedManagementDetail.refferenceNumber
                : 0,
            );
          } else {
            setHasDocuments(false);
          }

          const incidenceDetailText = incedenceTexts.find(
            (incidenceMatchingText) =>
              incidenceText.includes(incidenceMatchingText),
          );
          const claimText =
            formattedManagementDetail.registerType ===
            MANAGEMENTS_REGISTER_TYPE_CLAIMS
              ? MANAGEMENTS_REGISTER_TYPE_CLAIMS
              : undefined;

          setRequestStateData(
            getManagementsRequestTypeData(
              claimText ?? incidenceDetailText ?? claimTextSubType,
              formattedManagementDetail.state,
              isAgent ?? false,
              true,
            ),
          );
          setIsLoading(false);
          setStep((prevStep) =>
            formattedManagementDetail.state === MANAGEMENTS_STATE_PENDING ||
            formattedManagementDetail.state ===
              MANAGEMENTS_STATE_PENDING_CLIENT ||
            formattedManagementDetail.state ===
              MANAGEMENTS_STATE_PENDING_COMMERCIAL ||
            formattedManagementDetail.state ===
              MANAGEMENTS_STATE_PENDING_DISTRIBUTION ||
            formattedManagementDetail.state === MANAGEMENTS_STATE_OPEN
              ? prevStep
              : 3,
          );
          isUpdatePowersManagement &&
            setOwnerDataCup(
              ownerData?.cups.find(
                ({ codeCups }) =>
                  codeCups === formattedManagementDetail.cupsCode,
              ) || initialOwnerDataCup,
            );
        })
        .catch((error) => {
          setManagementDetail((prevManagementDetail) => ({
            ...prevManagementDetail,
            state: MANAGEMENTS_STATE_ERROR,
          }));
          setRequestStateData(
            getManagementsRequestTypeData(
              claimTextSubType,
              MANAGEMENTS_STATE_ERROR,
              isAgent ?? false,
            ),
          );
          setIsLoading(false);
        });
    },
    [incidenceText, ownerData?.cups],
  );

  useEffect(() => {
    const managementDetailAbortSignal = new AbortController();
    const isIncidenceText = incedenceTexts.some((incidenceMatchingText) =>
      incidenceText.includes(incidenceMatchingText),
    );
    const incidenceIconTitle = getIncidenceIconTitle(
      incedenceTexts,
      incidenceText,
    );
    if (userData && idIssue) {
      setIsLoading(true);
      setStep(1);
      setTitleHeader(
        isIncidenceText
          ? incidenceIconTitle.title
          : getManagementTitleText(recordTextType, claimTextSubType, idSubType),
      );
      getManagementDetail(
        +idIssue,
        userData.token,
        claimTextSubType,
        isUpdatePowersManagement,
        managementDetailAbortSignal.signal,
      );

      return () => managementDetailAbortSignal.abort();
    }
  }, [
    claimTextSubType,
    isUpdatePowersManagement,
    getManagementDetail,
    idIssue,
    recordTextType,
    userData,
    incidenceText,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      if (hasDocuments && refferenceNumber !== 0) {
        setIsLoadingFiles(true);
        if (userData) {
          try {
            const response = await getCallCenterDocuments(
              refferenceNumber,
              userData.token,
            );

            setFilesAttached((prevFiles) => {
              const newDocuments = response.filter(
                (doc) =>
                  !prevFiles.some(
                    (existingDoc) =>
                      existingDoc.IdDocumento === doc.IdDocumento,
                  ),
              );
              return [...prevFiles, ...newDocuments];
            });
          } catch (error) {
            console.error("Error downloading document:", error);
          }
          setIsLoadingFiles(false);
        }
      }
    };

    fetchData();
  }, [hasDocuments, refferenceNumber, userData]);

  const cupsInfo: ManagementCupsInfo = {
    cupsCode: managementDetail.cupsCode,
    address: managementDetail.address,
    environment: managementDetail.environment,
  };

  return (
    <Box component="section" className="managements-detail">
      <ManagementUpdateHeader
        title={titleHeader}
        isLoading={isUpdatePowersManagement && isLoading}
        step={step}
        showDetailCups={isUpdatePowersManagement}
        cupsDetail={{
          cupsCode: ownerDataCup.codeCups,
          rate: ownerDataCup.fee,
          address: ownerDataCup.address,
          service: ownerDataCup.envirorment,
        }}
      />
      <Stack
        component="article"
        direction="column"
        justifyContent="center"
        alignItems="flex-start"
        // alignItems="center"
        paddingTop={"2.5rem"}
        bgcolor={{ md: theme.palette.grey[50] }}
      >
        <ManagementsRequestState
          showManagementRequestInfo={showManagementRequestInfo}
          isLoading={isLoading}
          state={managementDetail.state}
          managementRequestInfo={managementDetail}
          title={requestStateData.title}
          description={requestStateData.description}
          descriptionExtra={requestStateData.descriptionExtra}
          loadingFiles={isLoadingFiles}
          files={filesAttached}
          cupsInfo={cupsInfo}
        />
      </Stack>
    </Box>
  );
};

export default ManagementsDetail;
